import React, { useEffect } from 'react';
import { useIntl } from 'umi';
import { Button, Form } from 'tea-component';
// import Abnormal from '@home/components/Abnormal';
// import TagFilterSelect from '@/pages/datalab/components/TagFilterSelect';
import SearchSingleSelect from '@/pages/datalab/components/SearchSingleSelect';
import type {
  IDatabaseSelectOption,
  IGameDatabaseItem,
  IGameTableInfoApiItem,
  IFieldConfigOption,
  ISchemaSelectOption,
  IStepSelectTablesProps,
} from '../../types';
import { DATABASE_ICON } from '../../constant';
import { getGameDatabase, getGameTableInfo } from '../../service';

import './index.scss';

/** Step1：数据库 / Schema / 数据表（单选下拉） */
const StepSelectTables: React.FC<IStepSelectTablesProps> = (props) => {
  const {
    gameCode,
    value,
    onChange,
    handleNext,
    prepareFieldConfigLoading = false,
  } = props;
  const { formatMessage } = useIntl();

  const {
    databaseValue,
    dataBaseOptions,
    dataBaseLoading,
    dataBaseError,
    schemaValue,
    schemaOptions,
    tablesLoading,
    tablesError,
    tablesValue,
    tablesOptions,
  } = value;

  const resolveTableName = (
    options: IFieldConfigOption[],
    tableId: string,
  ): string => {
    const opt = options.find((item) => item.value === tableId);
    if (opt && typeof opt.label === 'string' && opt.label) {
      return opt.label;
    }
    if (opt && typeof opt.text === 'string' && opt.text) {
      return opt.text;
    }
    return tableId;
  };

  const getDataBaseOptions = (
    data: IGameDatabaseItem[],
  ): IDatabaseSelectOption[] => {
    const dataBaseOptionsData = data?.map((item) => ({
      value: item?.database_uuid,
      label: item?.database_name,
      text: (
        <div className="dbl-options-text-box" key={item?.database_uuid}>
          <img
            className="dbl-options-text-box-icon"
            src={DATABASE_ICON?.[item?.driver]}
            alt=""
          />
          <span className="dbl-options-text-box-text">
            {item?.database_name}
          </span>
        </div>
      ),
    }));
    return dataBaseOptionsData ?? [];
  };

  const getSchemaOptions = (
    data: IGameDatabaseItem[],
    dataValue: string,
  ): ISchemaSelectOption[] => {
    if (!dataValue) {
      return [];
    }
    let schemaOptionsData: ISchemaSelectOption[] = [];
    data?.forEach((item) => {
      if (item?.database_uuid === dataValue) {
        schemaOptionsData = (item?.schema ?? []).map((schemaName) => ({
          value: schemaName,
          label: schemaName,
          text: schemaName,
        }));
      }
    });
    return schemaOptionsData;
  };

  /**
   * 编辑态 retrieve 已写入库/schema/表 id，但 Step1 未挂载时下拉 options 为空；
   * 库列表返回后补 schema 选项并拉表列表，避免回退 Step1 时无选项可展示。
   */
  const hydratePrefilledSelectOptions = (
    dataBaseList: IGameDatabaseItem[],
    prefilledDb: string,
    prefilledSchema: string,
    prefilledTableId?: string,
  ): void => {
    if (!prefilledDb) {
      return;
    }
    onChange({
      schemaOptions: getSchemaOptions(dataBaseList, prefilledDb),
    });
    if (prefilledSchema) {
      getDataResult(prefilledDb, prefilledSchema, prefilledTableId);
    }
  };

  const getDataBaseOptionsData = () => {
    const prefilledDb = databaseValue;
    const prefilledSchema = schemaValue;
    const prefilledTableId = tablesValue[0];
    onChange({ dataBaseLoading: true });
    getGameDatabase({ game_code: gameCode })
      .then((dataBaseList) => {
        onChange({
          dataBaseLoading: false,
          dataBaseError: false,
          dataBaseData: dataBaseList,
          dataBaseOptions: getDataBaseOptions(dataBaseList),
        });
        hydratePrefilledSelectOptions(
          dataBaseList,
          prefilledDb,
          prefilledSchema,
          prefilledTableId,
        );
      })
      .catch(() => {
        onChange({
          dataBaseLoading: false,
          dataBaseError: true,
        });
      });
  };

  /**
   * @param syncTableId 表列表返回后用于回填 tablesName 的 table_id；
   * 须由调用方在发起请求时捕获，勿在 then 内读闭包 tablesValue（schema 切换后可能过期）。
   */
  const getDataResult = (
    databaseUuid: string,
    schema: string,
    syncTableId?: string,
  ) => {
    onChange({
      tablesLoading: true,
      tablesError: false,
    });
    getGameTableInfo({
      game_code: gameCode,
      database_uuid: databaseUuid,
      schema: schema || '',
    })
      .then((tableList) => {
        const mapped = (tableList ?? []).map((item: IGameTableInfoApiItem) => ({
          value: item.table_id,
          label: item.table_name,
          text: item.table_name,
          table_desc: item.table_desc ?? '',
        })) as IFieldConfigOption[];
        const patch: {
          tablesOptions: IFieldConfigOption[];
          tablesLoading: boolean;
          tablesName?: string;
        } = {
          tablesOptions: mapped,
          tablesLoading: false,
        };
        if (syncTableId) {
          patch.tablesName = resolveTableName(mapped, syncTableId);
        }
        onChange(patch);
      })
      .catch(() => {
        onChange({
          tablesLoading: false,
          tablesError: true,
        });
      });
  };

  useEffect(() => {
    getDataBaseOptionsData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameCode]);

  const handleDatabaseChange = (val: string) => {
    if (val === databaseValue) {
      return;
    }
    onChange({
      databaseValue: val,
      schemaValue: '',
      tablesValue: [],
      tablesName: '',
      tablesOptions: [],
      schemaOptions: getSchemaOptions(value.dataBaseData, val),
    });
  };

  const handleSchemaChange = (val: string) => {
    if (val === schemaValue) {
      return;
    }
    onChange({
      schemaValue: val,
      tablesValue: [],
      tablesName: '',
      tablesOptions: [],
    });
    getDataResult(databaseValue, val);
  };

  // /** TagFilterSelect 多选；在此收敛为单表，并同步写入 tablesName */
  // const handleTableChange = (val: string[]): void => {
  //   const list = val || [];
  //   const options = tablesOptions || [];
  //   const patchTables = (next: string[]): void => {
  //     const tableId = next[0];
  //     onChange({
  //       tablesValue: next,
  //       tablesName: tableId ? resolveTableName(options, tableId) : '',
  //     });
  //   };
  //   if (list.length <= 1) {
  //     patchTables(list);
  //     return;
  //   }
  //   const prev = tablesValue || [];
  //   const added = list.filter((id) => !prev.includes(id));
  //   if (added.length >= 1) {
  //     patchTables([added[added.length - 1]]);
  //     return;
  //   }
  //   patchTables([list[list.length - 1]]);
  // };
  // const tablesState =
  //   tablesLoading || tablesError || tablesOptions?.length === 0;

  /** SearchSingleSelect 单选表；tablesValue 仍用数组（首项为主表 id）兼容向导 */
  const handleTableChange = (val: string): void => {
    const tableId = val || '';
    const options = tablesOptions || [];
    onChange({
      tablesValue: tableId ? [tableId] : [],
      tablesName: tableId ? resolveTableName(options, tableId) : '',
    });
  };

  const nextDisabled = !databaseValue || !schemaValue || !tablesValue?.length;

  return (
    <>
      <div className="dbl-model-agent-create-step dbl-model-agent-create-step-select-tables">
        <Form>
          <Form.Item
            required={true}
            showStatusIcon={false}
            label={formatMessage({ id: 'dbl_model_management_select_database' })}
            status={undefined}
            message={''}
          >
            <SearchSingleSelect
              value={databaseValue}
              onChange={handleDatabaseChange}
              className="dbl-model-single-select"
              selectorName={formatMessage({
                id: 'dbl_model_management_select_database',
              })}
              loading={dataBaseLoading}
              error={dataBaseError}
              options={dataBaseOptions}
              infoConfig={{
                selectorName: formatMessage({
                  id: 'dbl_model_management_select_database',
                }),
                selectorPlaceholder: formatMessage({
                  id: 'dbl_model_management_select_database',
                }),
                searchPlaceholder: formatMessage({
                  id: 'dbl_sql_add_search_database',
                }),
              }}
              selectProps={{
                matchButtonWidth: true,
              }}
            />
          </Form.Item>
          <Form.Item
            required={true}
            showStatusIcon={false}
            label={formatMessage({ id: 'dbl_model_management_select_schema' })}
            status={undefined}
            message={''}
          >
            <SearchSingleSelect
              options={schemaOptions}
              loading={dataBaseLoading}
              error={dataBaseError}
              value={schemaValue}
              onChange={handleSchemaChange}
              className="dbl-model-single-select"
              infoConfig={{
                selectorName: formatMessage({
                  id: 'dbl_model_management_select_schema',
                }),
                selectorPlaceholder: formatMessage({
                  id: 'dbl_model_management_select_schema',
                }),
                searchPlaceholder: formatMessage({
                  id: 'dbl_sql_add_search_schema',
                }),
              }}
              selectProps={{
                matchButtonWidth: true,
              }}
            />
          </Form.Item>
          <Form.Item
            required={true}
            showStatusIcon={false}
            label={formatMessage({ id: 'dbl_model_management_select_tables' })}
            status={undefined}
            message={''}
          >
            <SearchSingleSelect
              value={tablesValue[0] || ''}
              options={tablesOptions}
              loading={tablesLoading}
              error={tablesError}
              onChange={handleTableChange}
              className="dbl-model-single-select"
              infoConfig={{
                selectorName: formatMessage({
                  id: 'dbl_model_management_select_tables',
                }),
                selectorPlaceholder: formatMessage({
                  id: 'dbl_model_management_select_tables',
                }),
                searchPlaceholder: formatMessage({
                  id: 'dbl_sql_add_search_dataset',
                }),
              }}
              selectProps={{
                matchButtonWidth: true,
                listWidth: 400,
              }}
            />
            {/* <TagFilterSelect
              value={tablesValue}
              options={tablesOptions}
              listWidth={400}
              overlayClassName={`dbl-model-agent-create-tag-select dbl-model-tag-select ${tablesState ? 'dbl-model-tag-select-state' : ''}`}
              placeholder={formatMessage({
                id: 'dbl_model_management_select_tables',
              })}
              onChange={handleTableChange}
              name={formatMessage({ id: 'dbl_model_management_tables' })}
              tooltip={undefined}
              stateTips={
                tablesState && (
                  <Abnormal
                    style={{ height: '36px' }}
                    isLoading={tablesLoading}
                    isError={tablesError}
                    isNoData={tablesOptions?.length === 0}
                    nodataMsg={formatMessage({ id: 'dbl_common_select_no_data' })}
                  />
                )
              }
            /> */}
          </Form.Item>
        </Form>
      </div>
      <div className="dbl-model-agent-create-footer">
        <Button
          type="primary"
          disabled={nextDisabled || prepareFieldConfigLoading}
          loading={prepareFieldConfigLoading}
          onClick={handleNext}
        >
          {formatMessage({ id: 'dbl_model_management_create_model_next_step' })}
        </Button>
      </div>
    </>
  );
};

export default StepSelectTables;
